import { Component, ComponentFactoryResolver, Inject, Injector, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material//dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { BehaviorSubject, combineLatest, forkJoin, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Widget, WidgetUserFilter } from 'src/app/classes';
import { WIDGET_PROVIDER_SERVICE } from 'src/app/interfaces';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { User } from 'src/app/lib/user/classes';
import { FilterService } from 'src/app/services/filter.service';
import { FilterConditionsFormComponent } from '../filter-conditions-form/filter-conditions-form.component';
import { FilterFormComponent } from '../filter-form/filter-form.component';
import { ShareUserFilterFormComponent } from '../share-user-filter-form/share-user-filter-form.component';
import { WidgetComponentService } from '../widget/widget-component-service';
import { SHARE_WITH_CUSTOMERS, USERFILTER_FORMGROUP } from './classes';

@Component({
  selector: 'app-user-filter-dialog',
  templateUrl: './user-filter-dialog.component.html',
  styleUrls: ['./user-filter-dialog.component.css']
})
export class UserFilterDialogComponent implements OnDestroy, OnInit {

  _dynamicInner = new ReplaySubject<ViewContainerRef>(1);
  dynamicInner$ = this._dynamicInner.asObservable();
  @ViewChild('dynamicInner', { static: true, read: ViewContainerRef }) set dynamicInner(dynamicInner: ViewContainerRef) {
    this._dynamicInner.next(dynamicInner);
  }

  private _stepIndex = new BehaviorSubject(0);
  stepIndex$ = this._stepIndex.asObservable();

  steps: { label: string, view: Type<any>, visible: boolean }[] = [
    { label: 'Allgemein', view: FilterFormComponent, visible: true },
    { label: 'Filterkonditionen', view: FilterConditionsFormComponent, visible: true },
    { label: 'Filter teilen', view: ShareUserFilterFormComponent, visible: false },
  ];

  defaultFilterIcon: string = "filter_alt";
  defaultFilterBackgroundColor: string = "#ffffff";
  defaultFilterColor: string = "#222222";

  saveFinished: Subject<WidgetUserFilter>;

  formGroup: FormGroup;
  shareWithUsers = new FormArray([]);

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(WIDGET_PROVIDER_SERVICE) private _widgetComponentService: WidgetComponentService,
    @Inject(MAT_DIALOG_DATA) public userFilterIdentifier: number,
    private _sessionService: SessionService,
    private _ref: MatDialogRef<UserFilterDialogComponent>,
    private _formBuilder: FormBuilder,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _filterService: FilterService
  ) {
    this.formGroup = this._formBuilder.group(new WidgetUserFilter());
    this.formGroup.controls['filtertype'].disable();
  }

  close = () => this._ref.close(false);

  initRights = (soft: boolean = true) => {
    this._sessionService.hasRight('edit_datasets', soft).subscribe(result => this.steps[2].visible = result);
  }

  loadInner(view: ViewContainerRef, stepIndex: number) {
    view.clear();
    let step = this.steps[stepIndex];
    let componentFactory = this._componentFactoryResolver.resolveComponentFactory(step.view);
    view.createComponent(componentFactory, null, Injector.create({
      providers: [
        { provide: USERFILTER_FORMGROUP, useValue: this.formGroup },
        { provide: SHARE_WITH_CUSTOMERS, useValue: this.shareWithUsers },
        { provide: WIDGET_PROVIDER_SERVICE, useValue: this._widgetComponentService }
      ]
    }));
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      combineLatest([this._widgetComponentService.widget$, this._sessionService.user$]).subscribe(([widget, user]) => this.setUpForm(widget, user)),
      combineLatest([this.dynamicInner$, this.stepIndex$]).subscribe(([view, stepIndex]) => this.loadInner(view, stepIndex))
    ]);
  }

  saveFilter() {
    let obs = this._widgetComponentService.widget$
      .pipe(switchMap((widget: Widget) => ((this.userFilterIdentifier ? this._filterService.updateUserFilter(this.formGroup.getRawValue()).pipe(
        tap((result: WidgetUserFilter) => {
          let index = widget.widgetuserfilters.findIndex(x => x.filterid === result.filterid);
          if (index > -1) widget.widgetuserfilters[index] = result;
        })
      ) : this._filterService.createUserFilter(this.formGroup.getRawValue()).pipe(
        tap((result: WidgetUserFilter) => widget.widgetuserfilters.push(result))
      )))),
        switchMap((filter: WidgetUserFilter) => (this.shareWithUsers.value as User[]).length > 0? forkJoin((this.shareWithUsers.value as User[]).map(x => this._filterService.shareUserFilter(filter.filterid, x.id))): of(null)
        )
      );
    obs.subscribe(() => this._ref.close({ sharedWithUser: this.shareWithUsers.value, operation: this.userFilterIdentifier? 'UPDATE': 'CREATE' } as UserFilterDialogResult));
  }

  setUpForm(widget: Widget, user: User) {
    let filter = typeof this.userFilterIdentifier === 'number' ? widget.widgetuserfilters.find(x => x.filterid === this.userFilterIdentifier) : {
      filterwidget: widget.widgetid,
      filtericon: this.defaultFilterIcon,
      filtercolor: this.defaultFilterColor,
      filterbackgroundcolor: this.defaultFilterBackgroundColor,
      filterseqnr: Math.max(...widget.widgetuserfilters.map(x => x.filterseqnr), 0) + 1,
      filtertype: this._widgetComponentService.contentType,
      filteruser: user.id
    } as WidgetUserFilter;
    this.formGroup.patchValue(filter);
  }

  tabChanged = (event: MatTabChangeEvent) => this._stepIndex.next(event.index);

}

export class UserFilterDialogResult {
  sharedWithUser: User[];
  operation: 'CREATE' | 'UPDATE';
}
